"use client";

import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Grid,
  Collapse,
} from "@mui/material";
import { pxToVw } from "@/utils/pxToVw";
import { MOBILE_BASE } from "@/theme/theme";
import { useState } from "react";
import StickyImage from "./StickyImage";
import ExpandibleText from "./ExpandibleText";
import { useText } from "@/context/TextContext";

export default function OrigensSection() {
  const [expandedBlocks, setExpandedBlocks] = useState<{
    [key: number]: boolean;
  }>({});

  const handleToggle = (index: number) => {
    setExpandedBlocks((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const faqs = [
    {
      title: useText("nosaltres.historia.block1.title"),
      description: useText("nosaltres.historia.block1.text1"),
      descriptionExpanded: useText("nosaltres.historia.block1.text2"),
      images: [
        {
          alt: useText("nosaltres.historia.block1.image1footer"),
          src: useText("nosaltres.historia.block1.image1"),
          width: 344,
          height: 263,
          style: { width: "100%" },
        },
      ],
    },
    {
      title: useText("nosaltres.historia.block2.title"),
      description: useText("nosaltres.historia.block2.text1"),
      descriptionExpanded: useText("nosaltres.historia.block2.text2"),
    },
    {
      title: useText("nosaltres.historia.block3.title"),
      description: useText("nosaltres.historia.block3.text1"),

      images: [
        {
          alt: useText("nosaltres.historia.block3.image1footer"),
          src: useText("nosaltres.historia.block3.image1"),
          width: 226,
          height: 284,
          style: { width: "100%" },
        },
        {
          alt: useText("nosaltres.historia.block3.image2footer"),
          src: useText("nosaltres.historia.block3.image2"),
          width: 226,
          height: 284,
          style: { width: "100%" },
        },
      ],
    },
    {
      title: useText("nosaltres.historia.block4.title"),
      descriptionExpanded: useText("nosaltres.historia.block4.text2"),
      description: useText("nosaltres.historia.block4.text1"),
    },
    {
      title: useText("nosaltres.historia.block5.title"),
      description: useText("nosaltres.historia.block5.text1"),
    },
    {
      title: useText("nosaltres.historia.block6.title"),
      description: useText("nosaltres.historia.block6.text1"),
      descriptionExpanded: useText("nosaltres.historia.block6.text2"),
    },
  ];

  return (
    <Grid
      container
      pt={{ xs: pxToVw(80, MOBILE_BASE), md: pxToVw(194.93) }}
      mb={{ xs: pxToVw(40, MOBILE_BASE), md: pxToVw(100) }}
    >
      <Grid
        container
        size={{ xs: 10, md: 3 }}
        display={{ xs: "none", md: "flex" }}
      >
        <StickyImage
          width={344}
          height={445}
          alt="Historia"
          src={useText("nosaltres.historia.image")}
        />
      </Grid>
      {!isMobile && (
        <Grid
          container
          size={{ xs: 12, md: 6 }}
          offset={{ xs: 0, md: 3 }}
          direction={"column"}
        >
          {faqs.map((block, index) => {
            return <ExpandibleText key={index} index={index} {...block} />;
          })}
        </Grid>
      )}
      {isMobile && (
        <Grid
          size={{ xs: 12, md: 9 }}
          columns={{ xs: 12, md: 9 }}
          container
          spacing={{ xs: pxToVw(30, MOBILE_BASE), md: pxToVw(48) }}
          mb={{ xs: pxToVw(80, MOBILE_BASE), md: pxToVw(48) }}
        >
          {faqs.map((faq, index) => {
            const isExpanded = expandedBlocks[index] || false;

            return (
              <Grid
                key={index}
                size={{ xs: 12, md: 9 }}
                columns={{ xs: 12, md: 9 }}
                paddingTop={{ xs: pxToVw(10, MOBILE_BASE), md: pxToVw(10) }}
                borderTop={"1px solid #472600"}
                container
                spacing={{ xs: pxToVw(12, MOBILE_BASE), md: pxToVw(8) }}
                onClick={() => handleToggle(index)}
              >
                <Grid size={{ xs: 10, md: 8 }} container spacing={0}>
                  <Typography
                    variant="label1"
                    fontSize={{ xs: pxToVw(18, MOBILE_BASE), md: pxToVw(16) }}
                    lineHeight={{ xs: "110%", md: "120%" }}
                    color="marron"
                    sx={{ textTransform: "uppercase" }}
                    component={index === 0 ? "h1" : "h2"}
                    dangerouslySetInnerHTML={{ __html: faq.title }}
                  />
                </Grid>
                <Grid
                  size={{ xs: 2, md: 1 }}
                  container
                  justifyContent="flex-end"
                >
                  <Typography
                    variant="label1"
                    color="marron"
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    {isExpanded ? "↑" : "↓"}
                  </Typography>
                </Grid>
                {faq.description && isMobile && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Grid container>
                      <Typography
                        variant="body4"
                        color="marron"
                        className="historiaText"
                        mt={{ xs: pxToVw(0, MOBILE_BASE), md: pxToVw(16) }}
                        dangerouslySetInnerHTML={{
                          __html: faq.description + faq.descriptionExpanded,
                        }}
                      />
                    </Grid>
                  </Collapse>
                )}
              </Grid>
            );
          })}
        </Grid>
      )}
    </Grid>
  );
}
